'use client';

import type { RefObject } from 'react';

export interface ViewerFinderProps {
  query: string;
  /** Total matches across the document, for "3 of 12". */
  matchCount: number;
  /** 0-based index of the current match; ignored when matchCount is 0. */
  matchIndex: number;
  onChange: (query: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onClear: () => void;
  /** Owned by the panel so Ctrl/⌘F can focus this box from anywhere in it. */
  inputRef: RefObject<HTMLInputElement | null>;
}

/**
 * Find-in-document, asked for by the legal QAs.
 *
 * The browser's own Ctrl+F does not work here: the document is a rendered
 * PDF, and while pdf.js does lay a transparent text layer over it, the browser
 * find only searches pages that are MOUNTED — the canvas virtualises to a
 * window of five, so the browser would tell a reviewer a clause is not in the
 * document when it is simply on page 30. This searches the text of every page
 * (extracted once at load), and walks the matches by jumping the viewer to
 * each one.
 *
 * ITS OWN ROW, FULL WIDTH. The first cut was a 160px box sharing the page-nav
 * row with Prev/Next, Download and Open in Word — and the first reviewer to
 * use the panel reported "there is no finder". A control that exists but is
 * not seen has not shipped. A bar the width of the toolbar, with a magnifier,
 * is the shape people already recognise as "search this".
 *
 * Enter and Shift+Enter step forward and back, as in every editor a solicitor
 * has used; Escape clears. The count reads "3 of 12" rather than "12 matches"
 * because the question a reviewer is answering is "which one am I on".
 */
export function ViewerFinder({
  query, matchCount, matchIndex, onChange, onNext, onPrev, onClear, inputRef,
}: ViewerFinderProps) {
  const hasQuery = query.trim().length > 0;
  return (
    <div className="flex w-full items-center gap-2 rounded-md border border-line bg-stone-50 px-2 py-1 text-xs">
      <svg
        className="h-3.5 w-3.5 shrink-0 text-stone-400"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 10.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" />
      </svg>
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) onPrev();
            else onNext();
          } else if (e.key === 'Escape') {
            // Stop the panel's own Escape (which closes the viewer) — the
            // reviewer is dismissing the search, not the document.
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }
        }}
        placeholder="Find in document  (Ctrl+F)"
        aria-label="Find in document"
        className="min-w-0 flex-1 bg-transparent px-1 py-0.5 text-xs text-ink placeholder:text-stone-400 focus:outline-none"
      />
      {hasQuery && (
        <>
          <span
            className={`shrink-0 tabular-nums ${matchCount === 0 ? 'text-alarm' : 'text-muted'}`}
            aria-live="polite"
          >
            {matchCount === 0 ? 'No matches' : `${matchIndex + 1} of ${matchCount}`}
          </span>
          <button
            type="button"
            onClick={onPrev}
            disabled={matchCount === 0}
            aria-label="Previous match"
            className="rounded border border-stone-300 bg-surface px-1.5 py-0.5 text-content hover:bg-stone-100 disabled:opacity-40"
          >
            ▲
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={matchCount === 0}
            aria-label="Next match"
            className="rounded border border-stone-300 bg-surface px-1.5 py-0.5 text-content hover:bg-stone-100 disabled:opacity-40"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="rounded px-1 text-stone-400 hover:text-content"
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}
